import { api } from './client';

// Types for Page Versions Management
export interface PageVersionDto {
  tagSlug: string;
  currentNumber: number;
  domain?: string;
  locale?: string;
}

export interface PageVersionView {
  id: number;
  tagSlug: string;
  currentNumber: number;
  maxNumber: number;
  domain: string;
  locale: string;
  recoveredNumbers: number[] | null;
  updatedAt: string;
}

export interface PageVersionSetRequest {
  number: number;
}

export interface RedirectEntry {
  source: string;
  target: string;
}

function params(domain: string, locale: string): string {
  return `?domain=${encodeURIComponent(domain)}&locale=${encodeURIComponent(locale)}`;
}

// List all page versions for a (domain, locale)
export const listPageVersions = async (domain = 'onlyseeker', locale = 'en'): Promise<PageVersionView[]> => {
  const { data } = await api.get<PageVersionView[]>(`/admin/page-versions${params(domain, locale)}`);
  return data;
};

// Create a new page version (locale carried in payload)
export const createPageVersion = async (payload: PageVersionDto): Promise<PageVersionView> => {
  const { data } = await api.post<PageVersionView>('/admin/page-versions', payload);
  return data;
};

// Rotate to next version
export const rotatePageVersion = async (slug: string, domain = 'onlyseeker', locale = 'en'): Promise<PageVersionView> => {
  const { data } = await api.put<PageVersionView>(
    `/admin/page-versions/${encodeURIComponent(slug)}/rotate${params(domain, locale)}`
  );
  return data;
};

// Set specific version number
export const setPageVersion = async (slug: string, number: number, domain = 'onlyseeker', locale = 'en'): Promise<PageVersionView> => {
  const { data } = await api.put<PageVersionView>(
    `/admin/page-versions/${encodeURIComponent(slug)}/set${params(domain, locale)}`,
    { number } as PageVersionSetRequest
  );
  return data;
};

// Delete a page version
export const deletePageVersion = async (slug: string, domain = 'onlyseeker', locale = 'en'): Promise<void> => {
  await api.delete(`/admin/page-versions/${encodeURIComponent(slug)}${params(domain, locale)}`);
};

// Mark version as recovered
export const markRecovered = async (slug: string, number: number, domain = 'onlyseeker', locale = 'en'): Promise<PageVersionView> => {
  const { data } = await api.put<PageVersionView>(
    `/admin/page-versions/${encodeURIComponent(slug)}/recover${params(domain, locale)}`,
    { number }
  );
  return data;
};

// Unmark recovered version
export const unmarkRecovered = async (slug: string, number: number, domain = 'onlyseeker', locale = 'en'): Promise<PageVersionView> => {
  const { data } = await api.put<PageVersionView>(
    `/admin/page-versions/${encodeURIComponent(slug)}/unrecover${params(domain, locale)}`,
    { number }
  );
  return data;
};

// Quick switch to version
export const quickSwitch = async (slug: string, number: number, domain = 'onlyseeker', locale = 'en'): Promise<PageVersionView> => {
  const { data } = await api.put<PageVersionView>(
    `/admin/page-versions/${encodeURIComponent(slug)}/switch${params(domain, locale)}`,
    { number }
  );
  return data;
};

// Get redirect map for a (domain, locale)
export const getPageVersionRedirects = async (domain = 'onlyseeker', locale = 'en'): Promise<Record<string, string>> => {
  const { data } = await api.get<Record<string, string>>(`/admin/page-versions/redirects${params(domain, locale)}`);
  return data;
};

export const SUPPORTED_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'pt', name: 'Português' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
] as const;
